import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarClock, Check } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal, Kicker } from "../components/Reveal";
import { CALENDLY_URL, COMPLIANCE_SERVICES, FAQS } from "../data/content";
import { Faq } from "../components/Faq";
import { ContactForm } from "../components/ContactForm";

const CHALLENGES = [
  { audience: "Registered Investment Advisers", body: "Annual Rule 206(4)-7 reviews, marketing-rule exposure, SEC examination readiness, and the growing weight of state and federal filing obligations — often without a full-time CCO." },
  { audience: "Broker-Dealers", body: "Supervisory procedure design under Rule 3120, U4/U5 obligations, electronic communications review, AML programs, and FINRA cycle exams that demand documented evidence." },
  { audience: "Insurance Companies Offering Annuity Products", body: "Suitability processes, agent onboarding and continuing education, KYC/AML obligations, and disclosure maintenance across multiple state jurisdictions." },
  { audience: "Private Fund Managers", body: "Registration-versus-exemption analysis and right-sized compliance support that matches the firm's regulatory standing." },
];

const PROCESS = [
  { step: "Assess", body: "Risk assessment and gap analysis across your current program, benchmarked against SEC, FINRA, and state expectations." },
  { step: "Design", body: "Policies, procedures, and supervisory frameworks built for your business model — nothing more, nothing less." },
  { step: "Implement", body: "Training, filings, reviews, and meeting administration delivered with your team, not around it." },
  { step: "Sustain", body: "Ongoing monitoring of regulatory releases, annual reviews, and examination support that keeps the program current." },
];

const OUTCOMES = [
  "A documented, defensible compliance program",
  "Reduced regulatory and reputational risk",
  "Examination readiness — SEC, FINRA, and state",
  "A culture of compliance owned across the firm",
  "Freedom to focus on growing your business",
];

const Compliance = () => (
  <div data-testid="compliance-page">
    <PageHero
      kicker="Compliance Consulting"
      lines={[<span key="a">Compliance that</span>, <span key="b">protects <em className="italic text-[#3E9BDC]">growth.</em></span>]}
      intro="Our compliance team takes a proactive approach — helping you understand the rules and regulations that impact your lines of business to create a culture of compliance in your company. Let us manage your compliance program so you can focus on growing a successful business."
      meta={["RIAs", "Broker-Dealers", "Insurance & Annuity Companies", "Private Fund Managers"]}
    />

    <div className="bg-[#0A2B3D]" data-testid="compliance-hero-cta">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 pb-16 md:pb-20">
        <a href="#consultation" data-testid="compliance-hero-cta-button" className="btn-gold">
          Request a Consultation <ArrowRight size={16} />
        </a>
      </div>
    </div>

    <section className="py-20 md:py-28" data-testid="compliance-challenges">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <Kicker>The Challenges You Face</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight max-w-3xl leading-[1.15]">
            Regulation is ever growing and changing. Your program must keep pace.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          {CHALLENGES.map((c, i) => (
            <Reveal key={c.audience} delay={i * 0.06}>
              <div className="h-full p-8 md:p-10 bg-[#EBF3F8] border border-[#A9CFE2]/50" data-testid={`challenge-card-${i}`}>
                <h3 className="font-serif font-bold text-xl text-[#0A2B3D]">{c.audience}</h3>
                <p className="mt-3 text-sm text-[#475569] leading-relaxed">{c.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 md:py-28 border-t border-[#0A2B3D]/10" data-testid="compliance-capabilities">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <Kicker>Our Compliance Services</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight max-w-3xl leading-[1.15]">
            Decades of experience across the BD, RIA, private fund, and insurance spaces.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {COMPLIANCE_SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.06}>
              <div className="h-full p-8 md:p-10 bg-white border border-[#0A2B3D]/12 hover:border-[#0A2B3D]/35 transition-colors duration-500" data-testid={`capability-card-${i}`}>
                <h3 className="font-serif font-bold text-xl md:text-2xl text-[#0A2B3D]">{s.title}</h3>
                <ul className="mt-6 space-y-2.5">
                  {s.items.map((item) => (
                    <li key={item} className="flex gap-3 text-sm text-[#475569] leading-relaxed">
                      <Check size={15} className="text-[#3E9BDC] mt-0.5 shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="py-20 md:py-28 border-t border-[#0A2B3D]/10" data-testid="compliance-training">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-7">
          <Kicker>Compliance Training Programs</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight leading-[1.15]">
            Firm-wide compliance training, delivered through QuestCE.
          </h2>
          <p className="mt-6 text-[#475569] leading-relaxed max-w-2xl">
            Training is where a compliance program becomes culture. Led by Julie Bruno, our
            Compliance Training Programs practice administers Annual Compliance Meetings,
            questionnaires, and ongoing training for registered investment advisers — and manages
            broker-dealer compliance programs, including Firm Element continuing education —
            all delivered and tracked through QuestCE.
          </p>
          <p className="mt-4 text-[#475569] leading-relaxed max-w-2xl">
            From content planning to completion tracking, we run the program end to end, so your
            team stays current and your records stay examination-ready.
          </p>
          <Link to="/contact?topic=Compliance%20Consulting" data-testid="training-cta-link" className="btn-outline-dark mt-8">
            Discuss Training Programs <ArrowRight size={16} />
          </Link>
        </Reveal>
        <Reveal className="lg:col-span-5" delay={0.12}>
          <div className="bg-[#0A2B3D] text-white p-8 md:p-10 h-full" data-testid="training-coverage-card">
            <p className="eyebrow text-[#A9CFE2] mb-5">What We Manage</p>
            <ul className="space-y-3.5">
              {[
                "Annual Compliance Meetings — planning, content, and administration",
                "Compliance questionnaires and attestations",
                "Ongoing training programs for RIA teams",
                "Broker-dealer Firm Element continuing education",
                "QuestCE platform administration and reporting",
                "Completion tracking with examination-ready records",
              ].map((item) => (
                <li key={item} className="flex gap-3 text-sm text-white/75 leading-relaxed">
                  <Check size={15} className="text-[#3E9BDC] mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>

    <section id="aml-audits" className="bg-[#EBF3F8] py-20 md:py-28 border-t border-[#0A2B3D]/10" data-testid="aml-audits-section">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-7">
          <Kicker>AML Independent Audits</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight leading-[1.15]">
            Independent AML testing for broker-dealers — built for the online era.
          </h2>
          <p className="mt-6 text-[#475569] leading-relaxed max-w-2xl">
            American Elm provides AML independent audits for broker-dealers, delivering the
            objective annual testing FINRA expects — with findings that are practical, documented,
            and ready for examination. We specialize in audits and evaluations for BDs launching
            online platforms, and have guided new broker-dealers entering this fast-growing space
            from initial structuring through their first offerings.
          </p>
          <p className="mt-4 text-[#475569] leading-relaxed max-w-2xl">
            Whether you operate a traditional brokerage or a technology-forward platform, our
            auditors understand the customer journeys, onboarding flows, and funding mechanics that
            make online distribution different — and where regulators focus their attention.
          </p>
          <Link to="/aml-independent-audits" data-testid="aml-learn-more-link" className="btn-outline-dark mt-8">
            Explore AML Audit Services <ArrowRight size={16} />
          </Link>
        </Reveal>
        <Reveal className="lg:col-span-5" delay={0.12}>
          <div className="bg-white border border-[#0A2B3D]/12 p-8 md:p-10 h-full" data-testid="aml-capabilities-card">
            <p className="eyebrow text-[#3E9BDC] mb-5">Audit Coverage</p>
            <ul className="space-y-3.5">
              {[
                "Independent AML testing — FINRA Rule 3310",
                "AML program gap analysis and remediation",
                "Audits and evaluations for BDs launching online platforms",
                "New broker-dealer readiness reviews",
                "Customer identification and onboarding flow testing",
                "Written supervisory procedures for online distribution",
              ].map((item) => (
                <li key={item} className="flex gap-3 text-sm text-[#475569] leading-relaxed">
                  <Check size={15} className="text-[#3E9BDC] mt-0.5 shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="bg-[#0A2B3D] text-white py-20 md:py-28" data-testid="compliance-process">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <Kicker light>How We Work</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl tracking-tight max-w-2xl leading-[1.15]">
            A disciplined, four-stage engagement.
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {PROCESS.map((p, i) => (
            <Reveal key={p.step} delay={i * 0.08}>
              <div className="h-full border border-white/15 p-8 bg-[#082838] hover:border-[#3E9BDC]/60 transition-colors duration-500" data-testid={`process-step-${i}`}>
                <h3 className="font-serif font-bold text-2xl">{p.step}</h3>
                <p className="mt-3 text-sm text-white/65 leading-relaxed">{p.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-14">
          <div className="border border-white/15 p-8 md:p-10 bg-[#082838]" data-testid="compliance-outcomes">
            <p className="eyebrow text-[#A9CFE2] mb-5">Expected Outcomes</p>
            <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3">
              {OUTCOMES.map((o) => (
                <li key={o} className="flex gap-3 text-sm text-white/80">
                  <Check size={15} className="text-[#3E9BDC] mt-0.5 shrink-0" /> {o}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
      </div>
    </section>

    <section className="py-20 md:py-28" data-testid="compliance-expert-banner">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 md:px-12">
        <Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center bg-[#EBF3F8] border border-[#A9CFE2]/50 p-8 md:p-14">
            <div className="lg:col-span-8">
              <Kicker>Expert Witness Services</Kicker>
              <h2 className="mt-4 font-serif font-bold text-2xl sm:text-3xl text-[#0A2B3D] tracking-tight leading-snug">
                Consulting and testimony for arbitration and regulatory matters.
              </h2>
              <p className="mt-4 text-[#475569] leading-relaxed max-w-2xl">
                American Elm provides expert witness consulting and testimony for the investment and
                insurance industries — applying more than twenty years of experience interpreting the
                rules of FINRA, the SEC, and Departments of Insurance.
              </p>
            </div>
            <div className="lg:col-span-4 lg:text-right">
              <Link to="/expert-witness" data-testid="compliance-expert-link" className="btn-primary">
                Learn More <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>

    <Faq items={FAQS.compliance} id="compliance-faq" />

    <div className="max-w-4xl mx-auto px-5 sm:px-8 -mt-6 md:-mt-10 pb-4" data-testid="compliance-faq-explore-more">
      <Link
        to="/compliance-faq"
        data-testid="compliance-explore-more-link"
        className="inline-flex items-center gap-2 text-sm font-semibold text-[#3E9BDC] hover:text-[#0A2B3D] transition-colors underline underline-offset-4"
      >
        Explore more compliance questions <ArrowRight size={15} />
      </Link>
    </div>

    <section id="consultation" className="scroll-mt-24 bg-[#A9CFE2] py-16 md:py-24" data-testid="compliance-consultation">
      <div className="max-w-6xl mx-auto px-5 sm:px-8 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-start">
        <div>
          <Kicker>Request a Consultation</Kicker>
          <h2 className="mt-5 font-serif font-bold text-3xl sm:text-4xl text-[#0A2B3D] tracking-tight">Discuss your compliance program.</h2>
          <p className="mt-4 text-[#0A2B3D]/75 max-w-xl leading-relaxed">Tell us about your firm and a member of our team will contact you to discuss your needs. No obligation.</p>
          <a href={CALENDLY_URL} target="_blank" rel="noreferrer" data-testid="compliance-calendly-link" className="inline-flex items-center gap-2 mt-6 text-sm font-semibold text-[#0A2B3D] hover:text-[#082838] underline underline-offset-4">
            <CalendarClock size={16} /> Or schedule a 15-minute call
          </a>
        </div>
        <ContactForm compact withPhone defaultTopic="Compliance Consulting" defaultServices={["Compliance Consulting"]} />
      </div>
    </section>
  </div>
);

export default Compliance;
